import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Brain, Sparkles, Loader2, Trophy, AlertTriangle, Lightbulb, ListChecks, ArrowRight, FileText } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { Loader } from "@/components/common/Loader";
import { ModelPicker } from "@/components/common/ModelPicker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";

function Block({ icon: Icon, label, children, color }) {
    return (
        <div className="bg-obsidian border border-border rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
                <Icon className="w-4 h-4" style={{ color }} />
                <span className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">{label}</span>
            </div>
            <div className="text-sm text-foreground/90">{children}</div>
        </div>
    );
}

export default function Coach() {
    const [sessions, setSessions] = useState(null);
    const [reports, setReports] = useState([]);
    const [model, setModel] = useState("gpt-5.2");
    const [reviewType, setReviewType] = useState("daily");
    const [busy, setBusy] = useState(false);
    const [busyReview, setBusyReview] = useState(false);

    const load = async () => {
        const [c, r] = await Promise.all([api.get("/coach"), api.get("/reports")]);
        setSessions(c.data.sessions);
        setReports(r.data.reports);
    };
    useEffect(() => { load(); }, []);

    const generate = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/coach/generate", { model });
            if (data.generation_status === "fallback") toast.warning(data.generation_note || "AI was unavailable; deterministic guidance was used.");
            else toast.success("New debrief generated.");
            load();
        }
        catch { toast.error("Generation failed."); }
        finally { setBusy(false); }
    };
    const generateReview = async () => {
        setBusyReview(true);
        try {
            const { data } = await api.post("/reports/generate", { type: reviewType, model });
            if (data.generation_status === "fallback") toast.warning(data.generation_note || "AI was unavailable; deterministic guidance was used.");
            else toast.success(`${reviewType} review generated.`);
            load();
        }
        catch { toast.error("Generation failed."); }
        finally { setBusyReview(false); }
    };

    if (!sessions) return <Loader label="Loading AI coach" />;
    const latest = sessions[0];

    return (
        <div className="ww-fade-up">
            <PageHeader title="AI Coach" subtitle="Your post-session debrief and periodic performance reviews.">
                <ModelPicker value={model} onChange={setModel} />
                <button data-testid="generate-coach" onClick={generate} disabled={busy}
                    className="h-9 px-5 rounded-sm bg-volt text-black font-semibold hover:bg-[#33ceff] flex items-center gap-2 disabled:opacity-60">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} New Debrief
                </button>
            </PageHeader>

            {latest && (
                <div className="bg-surface border border-border rounded-sm p-6 mb-6" data-testid="latest-debrief">
                    {latest.generation_status === "fallback" && (
                        <div className="mb-4 rounded-sm border border-warn/40 bg-warn/10 px-3 py-2 text-xs text-warn">
                            Deterministic fallback shown — the selected AI provider was unavailable.
                        </div>
                    )}
                    <h3 className="font-heading text-lg font-bold flex items-center gap-2 mb-4"><Brain className="w-5 h-5 text-volt" /> Latest Debrief</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <Block icon={Trophy} label="Best Play" color="#00E676">{latest.best_play}</Block>
                        <Block icon={AlertTriangle} label="Biggest Mistake" color="#FF3366">{latest.biggest_mistake}</Block>
                    </div>
                    <div className="mt-3"><Block icon={Lightbulb} label="Most Valuable Lesson" color="#FFB000">{latest.lesson}</Block></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                        <Block icon={ListChecks} label="Three Areas To Improve" color="#00C2FF">
                            <ul className="space-y-1.5">
                                {latest.areas_to_improve?.map((a, i) => (
                                    <li key={i} className="flex gap-2"><span className="text-volt font-mono">{i + 1}.</span><span>{a}</span></li>
                                ))}
                            </ul>
                        </Block>
                        <Block icon={ArrowRight} label="One Action For Next Session" color="#00C2FF">{latest.next_action}</Block>
                    </div>
                </div>
            )}

            <div className="bg-surface border border-border rounded-sm p-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
                    <h3 className="font-heading text-lg font-bold flex items-center gap-2"><FileText className="w-5 h-5 text-volt" /> Reviews</h3>
                    <div className="flex items-center gap-2">
                        <Select value={reviewType} onValueChange={setReviewType}>
                            <SelectTrigger className="w-[130px] h-9 bg-obsidian border-border rounded-sm text-xs" data-testid="review-type"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-surface2 border-border">
                                <SelectItem value="daily">Daily</SelectItem>
                                <SelectItem value="weekly">Weekly</SelectItem>
                                <SelectItem value="monthly">Monthly</SelectItem>
                            </SelectContent>
                        </Select>
                        <button data-testid="generate-review" onClick={generateReview} disabled={busyReview}
                            className="h-9 px-4 rounded-sm border border-volt/50 text-volt hover:bg-volt/10 font-semibold flex items-center gap-2 disabled:opacity-60 text-sm">
                            {busyReview ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate
                        </button>
                    </div>
                </div>
                <div className="space-y-3">
                    {reports.map((r) => (
                        <div key={r.id} className="bg-obsidian border border-border rounded-sm p-4" data-testid={`review-${r.id}`}>
                            <div className="flex items-center justify-between gap-3">
                                <div className="flex items-center gap-2 min-w-0">
                                    <span className="text-[10px] uppercase font-mono tracking-wider text-volt border border-volt/40 px-2 py-0.5 rounded-sm">{r.type}</span>
                                    <span className="text-white font-medium truncate">{r.title}</span>
                                </div>
                                <span className="font-mono text-volt text-sm shrink-0">{r.rating}</span>
                            </div>
                            <p className="text-sm text-muted-foreground mt-2">{r.summary}</p>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
